import React, { useState, useEffect, useCallback, useRef } from "react";
import { invoicingAPI } from "../../utils/invoicingAPI";
import { LoadingSpinner } from "../../components/Loaders";
import { EmptyState, ErrorState } from "../../components/EmptyStates";
import Pagination from "../../components/Pagination";
import { FiSearch, FiX, FiActivity, FiRefreshCw } from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./SalesActivity.css";

const formatRWF = (amount) => {
  const num = parseFloat(amount || 0);
  return (
    new Intl.NumberFormat("en-RW", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num) + " RWF"
  );
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-RW", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function SalesActivity() {
  const { t } = useTranslation();
  const [sales, setSales] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const searchTimer = useRef(null);

  const loadSales = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: currentPage, page_size: 25 };
      if (debouncedSearch) params.search = debouncedSearch;

      const response = await invoicingAPI.listSalesActivity(params);
      const data = response?.data || response;

      if (Array.isArray(data)) {
        setSales(data);
        setPagination(null);
      } else if (data?.results) {
        setSales(data.results);
        setPagination({
          total: data.count,
          page: currentPage,
          pages: Math.ceil(data.count / 25),
        });
      } else if (data?.sales) {
        setSales(data.sales);
        setPagination(data.pagination || null);
      } else {
        setSales([]);
      }
      setError(null);
    } catch (err) {
      setError(err?.message || "Failed to load sales activity");
      setSales([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [searchTerm]);

  useEffect(() => {
    loadSales();
  }, [loadSales]);

  return (
    <div className="page-content sales-activity-page">
      <div className="section-header">
        <h2>
          <FiActivity style={{ marginRight: 8 }} />
          Sales Activity
        </h2>
        <button className="btn btn-ghost" onClick={loadSales}>
          <FiRefreshCw /> Refresh
        </button>
      </div>

      {/* Search */}
      <div className="activity-filters">
        <div className="search-input-wrapper">
          <FiSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by invoice, customer, organization..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="clear-search" onClick={() => setSearchTerm("")}>
              <FiX />
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorState message={error} onRetry={loadSales} />
      ) : sales.length === 0 ? (
        <EmptyState
          title="No Sales Activity"
          message="No sales records found across organizations."
        />
      ) : (
        <>
          <div className="activity-table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Organization</th>
                  <th>Invoice #</th>
                  <th>Customer</th>
                  <th>Payment</th>
                  <th>Amount</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td className="fw-600">
                      {sale.organization_name || sale.organization?.name || "—"}
                    </td>
                    <td>{sale.invoice_number || "—"}</td>
                    <td>{sale.customer_name || "—"}</td>
                    <td>
                      <span
                        className={`payment-badge payment-${(sale.payment_method || "other").toLowerCase()}`}
                      >
                        {sale.payment_method || "—"}
                      </span>
                    </td>
                    <td className="fw-600">{formatRWF(sale.total_amount)}</td>
                    <td>{formatDate(sale.created_at || sale.sale_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.pages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              onPageChange={setCurrentPage}
            />
          )}
        </>
      )}
    </div>
  );
}

export default SalesActivity;
